import {Form, Formik, FormikHelpers} from "formik";
import Layout from "../../components/Layout";
import {
    Button,
    capitalize,
    Chip,
    DialogActions,
    DialogContent,
    Paper,
    Typography,
} from "@mui/material";
import * as yup from "yup";
import React, {useCallback} from "react";
import {useNavigate, useParams} from "react-router";
import SubmitButton from "../../components/SubmitButton";
import {useTranslation} from "react-i18next";
import {
    useApproveAbwesenheit,
    useUpdateAbwesenheit,
} from "../../client/hooks";
import {useMutation, useQueryClient} from "react-query";
import {AbwesenheitDaten, Benutzer} from "../../api";
import AbwesenheitenFieldsContent from "../../components/abwesenheiten/AbwesenheitenFieldsContent";
import {useAbwesenheit} from "../../client/queries";
import ErrorAlert from "../../components/ErrorAlert";
import {notPermitted} from "../../errors";
import Guard, {usePermitted} from "../../components/Guard";
import DeleteAbwesenheitButton from "../../components/abwesenheiten/DeleteAbwesenheitButton";
import DeleteIcon from "@mui/icons-material/Delete";

export default function DetailsAbwesenheit() {
    const {t: translation} = useTranslation("customer");
    const {id: abwesenheitId = ""} = useParams<{
        id: string;
    }>();
    const abwesenheit = useAbwesenheit(abwesenheitId);
    const {mutateAsync} = useSaveAbwesenheit(abwesenheitId);
    const approveAbwesenheit = useApproveAbwesenheit();
    const permittedForUpdateAfterwards: boolean = usePermitted({
        permission: "abwesenheit:edit:afterwards",
    });

    let schema = yup.object().shape({
        start: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        ende: yup
            .date()
            .required("Datum ist erforderlich!")
            .max(
                new Date("9999-12-31"),
                "Datum darf nicht über dem 31.12.9999 liegen!"
            ),
        type: yup.string(),
        mitarbeiter: yup.object().required(),
        bemerkung: yup.string(),
        brzKey: yup
            .string()
            .nullable() // Erlaubt null, da das Feld optional ist
            .when("type", {
                is: "Arbeitszeitkonto",
                then: (schema) => schema.notRequired(),
                otherwise: (schema) =>
                    schema.required("BRZ-Schlüssel ist erforderlich!"),
            }),
    });
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const onSubmit = useCallback(
        async (
            values: {
                start: string;
                ende: string;
                mitarbeiter: any;
                bemerkung: string;
                type: string;
                brzKey: string;
            },
            formikHelpers: FormikHelpers<{
                start: string;
                ende: string;
                mitarbeiter: any;
                bemerkung: string;
                type: string;
                brzKey: string;
            }>
        ) => {
            const {setSubmitting, setStatus} = formikHelpers;
            const mitarbeiterId =
                typeof values.mitarbeiter === "string"
                    ? values.mitarbeiter
                    : (values.mitarbeiter as Benutzer).id ?? "";
            if (mitarbeiterId !== "") {
                if (values.start && values.ende) {
                    await mutateAsync({
                        mitarbeiterId: mitarbeiterId,
                        start: values.start,
                        ende: values.ende,
                        type: values.type ?? "urlaub",
                        bemerkung: values.bemerkung,
                        ...(values.brzKey !== "" && {brzKey: values.brzKey}),
                        status: abwesenheit.status,
                    });
                    navigate(`/abwesenheit`);
                }
            }
            setStatus(undefined);
            try {
            } catch (error: any) {
                setStatus(error.message);
            } finally {
                setSubmitting(false);
            }
        },
        [navigate, mutateAsync]
    );

    async function approve(values: {
        start: string;
        ende: string;
        mitarbeiter: any;
        bemerkung: string;
        type: string;
        brzKey: string;
    }) {
        if (values.start !== "" && values.ende !== "") {
            const mitarbeiterId =
                typeof values.mitarbeiter === "string"
                    ? values.mitarbeiter
                    : (values.mitarbeiter as Benutzer).id ?? "";
            if (mitarbeiterId !== "") {
                if (values.start && values.ende) {
                    await approveAbwesenheit(abwesenheitId, {
                        mitarbeiterId: mitarbeiterId,
                        start: values.start,
                        ende: values.ende,
                        type: values.type ?? "urlaub",
                        bemerkung: values.bemerkung,
                        status: "ANGENOMMEN",
                        ...(values.brzKey !== "" && {brzKey: values.brzKey}),
                    });
                    await queryClient.invalidateQueries(["abwesenheiten", abwesenheitId]);
                    navigate(`/abwesenheit`);
                }
            }
        }
    }

    return (
        <Layout title={"Abwesenheit"} onBackClick={() => navigate(-1)}>
            <Guard
                permission={"abwesenheit:edit"}
                fallback={<ErrorAlert error={notPermitted()}/>}
            >
                <Paper
                    elevation={1}
                    sx={{
                        marginTop: 2,
                        padding: 3,
                        marginLeft: 2,
                        marginRight: 2,
                        position: "relative",
                    }}
                >
                    <DeleteAbwesenheitButton id={abwesenheitId}></DeleteAbwesenheitButton>
                    <Formik
                        initialValues={{
                            start: abwesenheit.start ?? "",
                            ende: abwesenheit.ende ?? "",
                            mitarbeiter: abwesenheit.mitarbeiterId,
                            bemerkung: abwesenheit.bemerkung ?? "",
                            type: abwesenheit.type ?? "",
                            brzKey: abwesenheit.brzKey ?? "",
                        }}
                        validationSchema={schema}
                        onSubmit={onSubmit}
                    >
                        {({
                              isSubmitting,
                              isValidating,
                              isValid,
                              dirty,
                              values,
                          }) => (
                            <>
                                <Typography variant="h5">
                                    {abwesenheit.geloeschtAm === undefined ? (
                                        <>{abwesenheit.type}</>
                                    ) : (
                                        <>
                                            <>{abwesenheit.type}</>
                                            <Chip
                                                size="small"
                                                icon={<DeleteIcon/>}
                                                label={"gelöscht"}
                                                color="error"
                                            />
                                        </>
                                    )}
                                </Typography>
                                <DialogContent>
                                    <Form id="new-abwesenheit">
                                        <AbwesenheitenFieldsContent
                                            isVerwaltung={true}
                                            isNew={false}
                                        />
                                    </Form>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="success"
                                        onClick={() => approve(values)}
                                        disabled={
                                            isValidating || !isValid || abwesenheit.status !== "OFFEN"
                                        }
                                    >
                                        Speichern & Freigeben
                                    </Button>
                                    <SubmitButton
                                        form="new-abwesenheit"
                                        type="submit"
                                        variant="contained"
                                        color="secondary"
                                        loading={isSubmitting}
                                        disabled={
                                            isValidating ||
                                            !isValid ||
                                            !dirty ||
                                            !(
                                                abwesenheit.status !== "OFFEN" &&
                                                permittedForUpdateAfterwards
                                            )
                                        }
                                    >
                                        {capitalize(translation("save"))}
                                    </SubmitButton>
                                </DialogActions>
                            </>
                        )}
                    </Formik>
                </Paper>
            </Guard>
        </Layout>
    );
}

function useSaveAbwesenheit(id: string) {
    const updateAbwesenheit = useUpdateAbwesenheit();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation(
        (input: AbwesenheitDaten) => {
            return updateAbwesenheit(id, input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["abwesenheiten", id]);
                navigate(`/abwesenheiten`);
            },
        }
    );
}
